import React, { useState, useEffect } from 'react';

interface MentionUser {
  id: string;
  username: string;
  displayName: string;
  profilePicture?: string;
}

interface MentionSelectorProps {
  isOpen: boolean;
  searchQuery: string;
  position: { top: number; left: number };
  onSelectMention: (user: MentionUser) => void;
  onClose: () => void;
}

const MentionSelector: React.FC<MentionSelectorProps> = ({
  isOpen,
  searchQuery,
  position,
  onSelectMention,
  onClose
}) => {
  const [users, setUsers] = useState<MentionUser[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Mock data - à remplacer par une vraie recherche d'utilisateurs
  const mockUsers: MentionUser[] = [
    { id: '1', username: 'jupiter_whale', displayName: 'Jupiter Whale 🐋', profilePicture: undefined },
    { id: '2', username: 'solana_trader', displayName: 'Solana Trader', profilePicture: undefined },
    { id: '3', username: 'defi_master', displayName: 'DeFi Master', profilePicture: undefined },
    { id: '4', username: 'crypto_analyst', displayName: 'Crypto Analyst', profilePicture: undefined },
    { id: '5', username: 'nft_collector', displayName: 'NFT Collector', profilePicture: undefined }
  ];

  useEffect(() => {
    if (searchQuery) {
      const filtered = mockUsers.filter(user => 
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.displayName.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setUsers(filtered.slice(0, 5)); // Limiter à 5 résultats
      setSelectedIndex(0);
    } else {
      setUsers([]);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || users.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % users.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + users.length) % users.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (users[selectedIndex]) {
            onSelectMention(users[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, users, selectedIndex, onSelectMention, onClose]);

  if (!isOpen || users.length === 0) return null;

  return (
    <div
      className="fixed z-50 bg-gray-800/95 backdrop-blur-sm border border-emerald-700/50 rounded-lg shadow-lg max-w-xs w-64"
      style={{
        top: position.top,
        left: position.left
      }}
    >
      <div className="p-2">
        {users.map((user, index) => (
          <div
            key={user.id}
            className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-colors ${
              index === selectedIndex 
                ? 'bg-emerald-700/30 text-emerald-100' 
                : 'text-emerald-200 hover:bg-emerald-800/20'
            }`}
            onClick={() => onSelectMention(user)}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-700 to-cyan-600 flex items-center justify-center text-xs font-bold text-emerald-100">
              {user.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={user.displayName}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                user.displayName.charAt(0)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{user.displayName}</div>
              <div className="text-xs text-emerald-400 truncate">@{user.username}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MentionSelector;
